import { Fragment } from 'react'
import type { FunnelRow } from '../../lib/api'
import type { Variant, Audience } from '../../types'
import { stepLabel } from '../stepLabels'

interface Props {
  variant: Variant
  audience?: Audience
  rows: FunnelRow[] | null
  activeExcluded?: number
  resumeLeads?: number
  loading?: boolean
}

export function FunnelChart({ variant, audience, rows, activeExcluded, resumeLeads, loading }: Props) {
  if (loading || !rows) {
    return <div className="empty-state">Indlæser…</div>
  }
  if (rows.length === 0) {
    return <div className="empty-state">Ingen data i denne periode.</div>
  }

  // Baseline = antal sessioner der nåede første step. Tre lag af fallback:
  //   1. rows[0].views (normal vej — pageview-event)
  //   2. rows[0].submits (hvis pageview tabte race'et mod /api/session, men
  //      brugeren stadig klikkede start — submit implicerer view)
  //   3. max(views) på tværs af alle rows (hvis intro-events blev droppet
  //      men senere steps kom igennem, fx ved at /api/session var langsom)
  // Uden disse fallbacks ville en enkelt dropped intro/view-event lukke
  // hele funnel-rapporten ned med "Ingen sessions registreret endnu".
  const firstRow = rows[0]
  const maxViewsAnywhere = Math.max(...rows.map((r) => r.views))
  const baseline = Math.max(firstRow?.views ?? 0, firstRow?.submits ?? 0, maxViewsAnywhere)
  if (baseline === 0) {
    if (activeExcluded && activeExcluded > 0) {
      return (
        <div className="empty-state muted">
          {activeExcluded} {activeExcluded === 1 ? 'session er' : 'sessioner er'} stadig aktive lige nu — se <strong>Live visning</strong> ovenfor. Funnel'en opdateres når brugeren afslutter eller går idle.
        </div>
      )
    }
    return <div className="empty-state muted">Ingen sessions registreret endnu.</div>
  }

  const final = rows[rows.length - 1]?.views || 0
  const conversionPct = (final / baseline) * 100

  // "Worst drop" beregnes på baseline-justerede tal: hvis et step er betinget
  // (= nogle brugere SPRINGER det over, ikke dropper) så trækker vi skip-tallet
  // fra det forrige steps views FØR vi udregner drop-procenten. Ellers ville
  // calc/unge altid markere `maanedlig_praemie` som "største drop" — selvom
  // de "missing" brugere blot tog den alternative rute (har ikke bil).
  //
  // Sekvenser af betingede steps med samme skipReason (fx chat's 4-i-træk
  // postnr/age/currentPrice/km når bruger valgte "ingen forsikring") trækker
  // skip-tallet fra KUN ved indgangen til sekvensen — efter det er skipperne
  // allerede ude af populationen og må ikke trækkes fra igen.
  let worstStep = ''
  let worstDropPct = 0
  for (let i = 1; i < rows.length; i++) {
    const row = rows[i]
    const prev = rows[i - 1]
    const isBranchEntry = row.conditional && !(prev.conditional && prev.skipReason === row.skipReason)
    const skipDeduct = isBranchEntry ? (row.skippedHere ?? 0) : 0
    const eligible = Math.max(0, prev.views - skipDeduct)
    if (eligible <= 0) continue
    const dropPct = ((eligible - row.views) / eligible) * 100
    if (dropPct > worstDropPct) {
      worstDropPct = dropPct
      worstStep = row.step
    }
  }

  return (
    <div className="funnel-chart">
      {activeExcluded && activeExcluded > 0 ? (
        <div className="funnel-active-banner">
          <span className="live-dot live-dot-active" aria-hidden /> {activeExcluded} {activeExcluded === 1 ? 'aktiv session' : 'aktive sessioner'} ekskluderet — vises i Live ovenfor og rykker ind i funnel'en når de afslutter
        </div>
      ) : null}
      {resumeLeads && resumeLeads > 0 ? (
        <div className="funnel-active-banner">
          + {resumeLeads} {resumeLeads === 1 ? 'lead' : 'leads'} fra resumede sessioner (startet uden for perioden) — vises i Leads-tabellen men tæller ikke i funnel-procenterne
        </div>
      ) : null}
      <div className="funnel-summary">
        <div className="funnel-summary-rate">
          <div className="funnel-summary-rate-num">{conversionPct.toFixed(1)}%</div>
          <div className="funnel-summary-rate-label">Total konvertering</div>
        </div>
        <div className="funnel-summary-meta">
          <div className="funnel-summary-meta-line">
            <strong>{final}</strong> {final === 1 ? 'lead' : 'leads'} af <strong>{baseline}</strong> {baseline === 1 ? 'bruger' : 'brugere'}
          </div>
          {worstStep && (
            <div className="funnel-summary-meta-line muted small">
              Største drop: <strong>{stepLabel(variant, worstStep, audience)}</strong> ({worstDropPct.toFixed(0)}%)
            </div>
          )}
        </div>
      </div>

      <div className="funnel-list">
        {rows.map((row, i) => {
          const value = row.views
          const prevRow = i > 0 ? rows[i - 1] : null
          const previous = prevRow?.views ?? value
          // Ved betingede steps: skippere er IKKE drop-offs; træk dem fra
          // baseline før vi beregner drop-procent + lost-bar. Det er hvor
          // ændringen tidligst gav en negativ drop-pct (telefon=4 vs postnr=3
          // i chat) eller misvisende "worst step"-markering på calc/unge.
          // KUN ved indgangen til en betinget sekvens trækkes skip-tallet
          // fra; efterfølgende betingede steps med samme reason har allerede
          // udelukket skipperne (hvilket gør prev.views det rigtige eligible-tal).
          const isBranchEntry = !!row.conditional && !(prevRow?.conditional && prevRow.skipReason === row.skipReason)
          const skipDeduct = isBranchEntry ? (row.skippedHere ?? 0) : 0
          const eligible = Math.max(0, previous - skipDeduct)
          const stepLost = Math.max(0, eligible - value)
          const dropPct = eligible > 0 ? (stepLost / eligible) * 100 : 0
          const fillPct = (value / baseline) * 100
          const lostThisStepPct = (stepLost / baseline) * 100
          const severity = dropPct >= 50 ? 'severe' : dropPct >= 20 ? 'warn' : 'mild'
          const isWorst = row.step === worstStep && worstDropPct > 0
          const isLead = i === rows.length - 1
          // Vis kun skip-konnektoren på FØRSTE betingede step i en sekvens.
          // Subsequent rows i samme branch får stadig "BETINGET"-badge men
          // sparer den gentagne "X sprang over"-tekst (samme tal, samme reason
          // — visuel støj uden ny information).
          const showSkip = isBranchEntry && (row.skippedHere ?? 0) > 0
          // Rejoin: forrige steps "overskydende" brugere kommer ind på dette
          // step via en alternativ rute. Detekteres som "current.views >
          // previous.views" for ikke-betingede steps efter et betinget step.
          // Eksempel: calc/unge resultat=10 efter selskab=7 → 3 brugere
          // re-joinede her fra "ingen bil"-genvejen.
          const joinedHere = !row.conditional && i > 0 && prevRow?.conditional && value > previous
            ? value - previous
            : 0

          return (
            <Fragment key={row.step}>
              {i > 0 && showSkip && (
                <div className="funnel-connector funnel-connector-skip">
                  <span className="connector-info">
                    <span className="connector-arrow" aria-hidden>↳</span>
                    <span>{row.skippedHere} {row.skippedHere === 1 ? 'bruger' : 'brugere'} sprang over</span>
                    {row.skipReason && <span className="connector-skip-reason">· {row.skipReason}</span>}
                  </span>
                </div>
              )}
              {i > 0 && stepLost > 0 && (
                <div className={`funnel-connector funnel-connector-${severity}${isWorst ? ' funnel-connector-worst' : ''}`}>
                  <span className="connector-info">
                    <span className="connector-arrow" aria-hidden>↓</span>
                    <span>−{stepLost} {stepLost === 1 ? 'bruger' : 'brugere'}</span>
                    <span className="connector-pct">· {dropPct.toFixed(0)}%</span>
                  </span>
                </div>
              )}
              {joinedHere > 0 && (
                <div className="funnel-connector funnel-connector-rejoin">
                  <span className="connector-info">
                    <span className="connector-arrow" aria-hidden>↪</span>
                    <span>+{joinedHere} {joinedHere === 1 ? 'bruger' : 'brugere'} fra alternativ rute</span>
                  </span>
                </div>
              )}
              {isLead && i > 0 && (
                <div className="funnel-lead-divider" aria-hidden>
                  <span className="funnel-lead-divider-line" />
                  <span className="funnel-lead-divider-label">Lead-konvertering</span>
                  <span className="funnel-lead-divider-line" />
                </div>
              )}
              <div className={`funnel-row${isWorst ? ' funnel-row-worst' : ''}${isLead ? ' funnel-row-lead' : ''}${row.conditional ? ' funnel-row-conditional' : ''}`}>
                <div className="funnel-step-num">{i + 1}</div>
                <div className="funnel-label">
                  <span className="funnel-label-text">{stepLabel(variant, row.step, audience)}</span>
                  {row.conditional && <span className="funnel-cond-badge" title={`Betinget step — springes over hvis: ${row.skipReason ?? ''}`}>BETINGET</span>}
                  {isLead && <span className="funnel-lead-badge">LEAD</span>}
                </div>
                <div className="funnel-bar-track">
                  <div
                    className="funnel-bar-fill"
                    style={{ width: `${Math.max(0, Math.min(100, fillPct))}%` }}
                  />
                  {lostThisStepPct > 0 && (
                    <div
                      className={`funnel-bar-lost funnel-bar-lost-${severity}`}
                      style={{ width: `${Math.min(100 - fillPct, lostThisStepPct)}%` }}
                    />
                  )}
                </div>
                <div className="funnel-count">{value}</div>
                <div className="funnel-pct">{Math.round(fillPct)}%</div>
              </div>
            </Fragment>
          )
        })}
      </div>
    </div>
  )
}
